/**
 * `dashboardsApi` — standalone RTK Query slice for the Dashboards page
 * (folder explorer + dashboards). Backed by the proxy-server
 * `dashboard_folders` / `dashboards` Postgres tables (see
 * `proxy-server/internal/ontology/migrate/047_dashboards_init.sql`) and
 * routed through the `axilon-api-service` BFF under `/api/ontology/*`
 * (`dashboards-routes.ts` → `ProxyDashboardsClient`), so the browser
 * never talks to the proxy directly.
 *
 * Split off from the legacy `ontologyApi` catch-all so the Dashboards
 * page owns its own cache + tag space (`'DashboardFolder'` /
 * `'Dashboard'`). Shares `ontologyBaseQuery` (auth + branch headers)
 * with the other ontology slices via `ontology-api-base.ts`; the
 * absolute `ONTOLOGY_SERVICE_URL` prefix (from `getOntologyServiceBaseUrl`)
 * targets the BFF instead of the `/api/v1/ontology` graph root the base
 * query is otherwise rooted at — same approach `ingestApi` uses.
 *
 * Both list calls return the full set: the sidebar needs every row to
 * derive its tree and the row volume is expected to stay small.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { getOntologyServiceBaseUrl } from '../../config'
import { ontologyBaseQuery } from '../core'
import type {
  CopyDashboardFolderInput,
  CopyDashboardFolderResponse,
  CopyDashboardInput,
  CreateDashboardFolderInput,
  CreateDashboardInput,
  Dashboard,
  DashboardFolder,
  MoveDashboardFolderInput,
  MoveDashboardFolderResponse,
  PatchDashboardInput,
  RenameDashboardInput,
  RestoreDashboardFolderInput,
  RestoreDashboardFolderResponse,
  UpdateDashboardCodeInput
} from '@atbash-labs/proxy-client'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export const DASHBOARDS_TAG_TYPES = ['DashboardFolder', 'Dashboard'] as const
export type DashboardsTagType = (typeof DASHBOARDS_TAG_TYPES)[number]

export const dashboardsApi = createApi({
  reducerPath: 'dashboardsApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: DASHBOARDS_TAG_TYPES,
  endpoints: (builder) => ({
    // ── Dashboard folders ───────────────────────────────────────────
    listDashboardFolders: builder.query<DashboardFolder[], void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/dashboard-folders`,
      transformResponse: (response: { folders?: DashboardFolder[] }) => response.folders ?? [],
      providesTags: ['DashboardFolder']
    }),
    createDashboardFolder: builder.mutation<DashboardFolder, CreateDashboardFolderInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboard-folders`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['DashboardFolder']
    }),
    renameDashboardFolder: builder.mutation<
      DashboardFolder,
      { id: string; input: RenameDashboardInput }
    >({
      query: ({ id, input }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboard-folders/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: input
      }),
      invalidatesTags: ['DashboardFolder']
    }),
    deleteDashboardFolder: builder.mutation<void, string>({
      query: (id) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboard-folders/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['DashboardFolder', 'Dashboard']
    }),
    moveDashboardFolder: builder.mutation<MoveDashboardFolderResponse, MoveDashboardFolderInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboard-folders/move`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['DashboardFolder', 'Dashboard']
    }),
    copyDashboardFolder: builder.mutation<CopyDashboardFolderResponse, CopyDashboardFolderInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboard-folders/copy`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['DashboardFolder', 'Dashboard']
    }),
    restoreDashboardFolder: builder.mutation<
      RestoreDashboardFolderResponse,
      RestoreDashboardFolderInput
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboard-folders/restore`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['DashboardFolder', 'Dashboard']
    }),
    deleteDashboardFolderByPath: builder.mutation<void, { path: string }>({
      query: ({ path }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboard-folders/delete`,
        method: 'POST',
        body: { path }
      }),
      invalidatesTags: ['DashboardFolder', 'Dashboard']
    }),

    // ── Dashboards ──────────────────────────────────────────────────
    listDashboards: builder.query<Dashboard[], void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/dashboards`,
      transformResponse: (response: { dashboards?: Dashboard[] }) => response.dashboards ?? [],
      providesTags: ['Dashboard']
    }),
    createDashboard: builder.mutation<Dashboard, CreateDashboardInput>({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboards`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['Dashboard']
    }),
    copyDashboard: builder.mutation<Dashboard, { id: string; input: CopyDashboardInput }>({
      query: ({ id, input }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboards/${encodeURIComponent(id)}/copy`,
        method: 'POST',
        body: input
      }),
      invalidatesTags: ['Dashboard', 'DashboardFolder']
    }),
    renameDashboard: builder.mutation<Dashboard, { id: string; input: PatchDashboardInput }>({
      query: ({ id, input }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboards/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: input
      }),
      invalidatesTags: ['Dashboard', 'DashboardFolder']
    }),
    deleteDashboard: builder.mutation<void, string>({
      query: (id) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboards/${encodeURIComponent(id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Dashboard']
    }),
    updateDashboardCode: builder.mutation<
      Dashboard,
      { id: string; input: UpdateDashboardCodeInput }
    >({
      query: ({ id, input }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/dashboards/${encodeURIComponent(id)}/code`,
        method: 'PUT',
        body: input
      }),
      invalidatesTags: ['Dashboard']
    })
  })
})

export const {
  useListDashboardFoldersQuery,
  useCreateDashboardFolderMutation,
  useRenameDashboardFolderMutation,
  useDeleteDashboardFolderMutation,
  useMoveDashboardFolderMutation,
  useCopyDashboardFolderMutation,
  useRestoreDashboardFolderMutation,
  useDeleteDashboardFolderByPathMutation,
  useListDashboardsQuery,
  useCreateDashboardMutation,
  useCopyDashboardMutation,
  useRenameDashboardMutation,
  useDeleteDashboardMutation,
  useUpdateDashboardCodeMutation
} = dashboardsApi

export type {
  CopyDashboardFolderInput,
  CopyDashboardFolderResponse,
  CopyDashboardInput,
  CreateDashboardFolderInput,
  CreateDashboardInput,
  Dashboard,
  DashboardFolder,
  MoveDashboardFolderInput,
  MoveDashboardFolderResponse,
  PatchDashboardInput,
  RenameDashboardInput,
  RestoreDashboardFolderInput,
  RestoreDashboardFolderResponse,
  UpdateDashboardCodeInput
}
